import React, { useState, useEffect } from 'react'
import cn from 'classnames'
import { values, isNumber } from 'lodash'
import { useHistory } from 'react-router-dom'

import OrderInformation from './OrderInformation'
import TabOrderTotals from '../TabOrderTotals'
import SidebarModal from '../../SidebarModal'
import Button from '../../Button'
import TipEntryModal from '../../TipEntryModal'
import { paymentIcons, resolvePaymentIcon } from '../../../utils/icons'
import { centsToDollars } from '../../../utils/formatters'
import { ReactComponent as CheckmarkIcon } from '../../../assets/icons/checkmark-icon.svg'
import useDeepCompareEffect from 'use-deep-compare-effect'
import useUpdatePayment from '../../../hooks/useUpdatePayment'


import styles from './FinalizeTender.module.scss'

const makeTipEntryModalTitle = (maxGratuity) => {
  if (isNumber(maxGratuity) && maxGratuity >= 0) {
    return `${centsToDollars(maxGratuity)} maximum amount`
  } else {
    return ''
  }
}

const isUsableNumber = (value) => isNumber(value) && value > 0

const Payment = ({ id, selected, finalized, cardType, label, onSelect, className }) => {
  const classNames = cn(styles.payment, { [styles.selected]: selected, [styles.finalized]: finalized }, className)
  const icon = React.cloneElement(resolvePaymentIcon(cardType)?.open ?? paymentIcons['Unknown Card']?.open, { className: styles.icon })
  let selectionIcon = (
    <span className={styles.selectionIcon}>
      <span className={styles.selectionIconInner}></span>
    </span>
  )

  if (finalized) {
    selectionIcon = <CheckmarkIcon className={styles.checkmarkIcon} />
  }

  const handleOnSelect = () => {
    if (finalized) return

    onSelect()
  }

  return (
    <li className={classNames} onClick={handleOnSelect}>
      {selectionIcon}
      {icon}
      <span>{label}</span>
    </li>
  )
}

const PaymentsList = ({ payments, onSelect, selectedPaymentId, className }) => {
  const classNames = cn(styles.paymentsList, className)

  return (
    <ul className={classNames}>
      {payments?.map?.((payment, index) => {
        const isSelected = selectedPaymentId === payment.id

        const handleOnCancel = () => {
          onSelect(payment?.id, index)
        }

        return (
          <Payment
            selected={isSelected}
            key={payment?.id ?? index}
            label={payment.label}
            cardType={payment.cardType}
            onSelect={handleOnCancel}
          />
        )
      })}
    </ul>
  )
}

const FinalizeTender = ({
  orderId,
  orderNumber,
  paymentPreference,
  onMount,
  modifiedAt,
  itemCount,
  suiteInformation,
  paymentDisplay,
  orderTotal,
  finalizeOrder,
  exemptionOverrides,
  maxGratuity,
  onClose,
  className,
  userNotes,
  payments,
  paymentId,
  pendingMutation,
}) => {
  const classNames = cn(styles.details, className)
  const history = useHistory()
  const [showGratuityEntry, setShowGratuityEntry] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState(paymentId)
  const [selectedTipAmount, setSelectedTipAmount] = useState(selectedPayment?.tip ?? 0)
  const grandTotal = centsToDollars(orderTotal?.totalAmountInCents + selectedTipAmount)
  const [updatePayment, { loading, failed, success }] = useUpdatePayment()
  const orderLoading = !(pendingMutation === undefined)

  useDeepCompareEffect(() => {
    const targetPayment = payments.find(payment => payment.id === paymentId)
    setSelectedPayment(targetPayment)
    setSelectedTipAmount(targetPayment?.tip ?? 0)
  }, [payments, paymentId])

  useEffect(() => {
    onMount()
  }, [])

  useEffect(() => {
    if (success) {
      history.push(`/receipt/${orderId}/${paymentId}`, { redirectTo: `/tabs/${orderId}` })
    }
  }, [success])

  const handleFinalize = () => {
    updatePayment({
      ...selectedPayment,
      orderId,
      paymentId,
      amountInCents: (selectedPayment.amountInCents - selectedPayment?.tip ?? 0) + selectedTipAmount,
      tip: selectedTipAmount,
    })
  }

  const handleSetNewTip = (tipInCents) => {
    const newTipAmount = isUsableNumber(maxGratuity) ? Math.min(maxGratuity, tipInCents) : tipInCents

    setSelectedTipAmount(newTipAmount)
  }

  const confirmGratuity = (
    <Button
      key="enter gratuity button"
      onClick={() => setShowGratuityEntry(true)}
      className={styles.actionButton}
      disabled={orderLoading}
    >
      Add Gratuity
    </Button>
  )

  const finalizeAmount = (
    <Button
      key="finalize amount button"
      onClick={() => handleFinalize()}
      className={[styles.actionButton, styles.mintGreenHighlight]}
      disabled={orderLoading}
    >
      Finalize Amount
    </Button>
  )

  const tipEntryModalTitle = makeTipEntryModalTitle(maxGratuity)
  let customGratuityEntry
  if (showGratuityEntry) {
    customGratuityEntry = (
    <TipEntryModal
      key="custom gratuity"
      title={tipEntryModalTitle}
      errorMessage={tipEntryModalTitle}
      initialValue={selectedTipAmount}
      maxTipInCents={maxGratuity}
      saveButtonText="Set Gratuity"
      onClose={() => setShowGratuityEntry(false)}
      onSubmit={handleSetNewTip}
    />
    )
  }

  let content = null

  if (!paymentId) {
    content = (
      <div className={styles.bodyContainer}>
        <p className={styles.selectTenderHeader}>Select a tender to finalize</p>
        <PaymentsList
          payments={payments}
          selectedPaymentId={selectedPayment?.id}
          onSelect={(_, index) => setSelectedPayment(payments[index])}
        />
        <div className={styles.buttonContainer}>
          {selectedPayment && <Button
            key="next"
            onClick={() => history.push(`${history.location.pathname}/${selectedPayment.id}`)}
            className={cn(styles.actionButton, styles.mintGreenHighlight)}
          >
            Next
          </Button>}
        </div>
      </div>
    )
  } else {
    content = (
      <div className={styles.bodyContainer}>
        <OrderInformation
          account={suiteInformation?.displayName}
          payment={selectedPayment?.shortDescription}
          grandTotal={orderTotal?.totalDisplay}
          tenderAmount={centsToDollars(selectedPayment?.amountInCents - selectedPayment.tip ?? 0)}
          orderNumber={orderNumber}
        />
        <TabOrderTotals
          className={styles.tabOrderTotals}
          exemptions={exemptionOverrides}
          discount={orderTotal?.discountDisplay}
          itemCount={itemCount}
          nonExemptableTaxRules={values(
            orderTotal?.nonExemptableTaxRules ?? []
          )}
          serviceCharge={orderTotal?.serviceChargeDisplay}
          serviceChargeName={orderTotal?.serviceChargeName ?? 'Service Charge'}
          subtotal={orderTotal?.subtotalDisplay}
          tax={orderTotal?.exemptableTaxDisplay}
          tip={centsToDollars(selectedTipAmount)}
          total={grandTotal}
          tenderAmount={centsToDollars(selectedPayment?.amountInCents - selectedPayment.tip ?? 0)}
          tenderTip={centsToDollars(selectedTipAmount)}
          tenderTotal={centsToDollars((selectedPayment?.amountInCents - selectedPayment.tip ?? 0) + selectedTipAmount)}
        />
        <div className={styles.buttonContainer}>
          {confirmGratuity}
          {finalizeAmount}
        </div>
      </div>
    )
  }

  return (
    <SidebarModal
      show
      title="Finalize Order"
      onClose={onClose}
      className={classNames}
    >
      {content}
      {customGratuityEntry}
    </SidebarModal>
  )
}

export default FinalizeTender
